import { CSSProperties, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { DefaultLanguage } from 'app/i18/i18'
import { RoutingPaths } from 'app/routes/routes'

import userStore from 'entities/user/module/user-store'

import { sendGtagEvent } from 'shared/lib/analytics'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'

import s from './styles.module.scss'

const phraseKeys = [
  'destination',
  'budget',
  'beach',
  'unsure',
  'preferences',
] as const

export const BannerChat = observer(() => {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isLeaving, setIsLeaving] = useState(false)
  const lang = userStore.getClientStateString('language') || DefaultLanguage

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let timer: ReturnType<typeof setInterval> | undefined
    let transitionTimer: ReturnType<typeof setTimeout> | undefined

    const restart = () => {
      clearInterval(timer)
      clearTimeout(transitionTimer)
      setActiveIndex(0)
      setIsLeaving(false)
      if (!reducedMotion.matches) {
        timer = setInterval(() => {
          setIsLeaving(true)
          transitionTimer = setTimeout(() => {
            setActiveIndex((index) => (index + 1) % phraseKeys.length)
            setIsLeaving(false)
          }, 150)
        }, 3000)
      }
    }

    restart()
    reducedMotion.addEventListener('change', restart)
    return () => {
      clearInterval(timer)
      clearTimeout(transitionTimer)
      reducedMotion.removeEventListener('change', restart)
    }
  }, [])

  return (
    <Link
      to={RoutingPaths.touraika.path()}
      state={{ lang }}
      className={s.card}
      aria-label={t('tours_block.touraika.title')}
      onClick={() => sendGtagEvent('TOURS_TURAIKA_CLICK')}
    >
      <svg
        className={s.outline}
        aria-hidden="true"
        focusable="false"
      >
        <rect
          className={s.outlineTrack}
          x="1.25"
          y="1.25"
          rx="22.75"
        />
        {Array.from({ length: 16 }, (_, index) => (
          <rect
            key={index}
            className={clsx(s.outlineHighlight, index < 4 && s.outlineTint)}
            x="1.25"
            y="1.25"
            rx="22.75"
            pathLength={100}
            strokeDasharray={`${48 - index * 2} ${52 + index * 2}`}
            strokeOpacity={1 / (16 - index)}
            style={{ '--outline-offset': `${-index}px` } as CSSProperties}
          />
        ))}
      </svg>
      <span
        className={s.icon}
        aria-hidden="true"
      >
        <AppIcon
          id={AllIcons.TOURAIKA}
          className={s.iconGraphic}
          width={32}
          height={32}
        />
      </span>
      <span className={s.content}>
        <span className={clsx('medium-14', s.title)}>
          {t('tours_block.touraika.title')}
        </span>
        <span
          className={clsx('semi-bold-14', s.phrases, isLeaving && s.leaving)}
        >
          {phraseKeys.map((key, index) => (
            <span
              key={key}
              className={clsx(s.phrase, index === activeIndex && s.active)}
              aria-hidden={index !== activeIndex}
            >
              {t(`tours_block.touraika.${key}`)}
            </span>
          ))}
        </span>
      </span>
      <AppIcon
        className={s.chevron}
        id={AllIcons.CHEVRON_CELL}
        color="var(--grey-soft)"
        width={24}
        height={24}
      />
    </Link>
  )
})
